/**
 * 分类管理对话框 —— **自建分类的增 / 改名 / 移除**（批 `112-A` · `DR-062`）
 *
 * 依据：B 域 PRD **§5.3.3**（自建分类）· **`AC-B3.6` / `B3.3` / `B3.5` / `B3.8` / `B3.13`–`B3.17`** ·
 * **`DR-062`**（六条口径）· `DR-020` B6（入口在分类选择器，不在设置页）
 *
 * ## 🔴 为什么拆成「Body + 外壳」两个导出（铁律 121）
 *
 * MUI `Dialog` 走 **Portal** ⇒ `renderToStaticMarkup` 里**拿不到弹窗内容**
 * （批 105 已踩过一次：静态渲染找不到内容，是**假红**）。
 * ⇒ 内容层单独导出 `CategoryManageBody`（零 Portal · 零副作用 · 回调由调用方注入），
 * spec 直接渲染它；外壳 `CategoryManageDialog` 只负责 `Dialog` 与数据接线。
 *
 * ## 🔴 三条文案不能随便改
 *
 * | 位置 | 必须这么说 | 为什么 |
 * |---|---|---|
 * | 移除按钮 | 「**从分类列表移除**」+ 副文案「已记过的账不受影响，历史里仍显示该分类」 | 服务端是**软删**（`DR-062` D3），说「删除」就是在撒谎 |
 * | 系统分类 | 置灰 + 「系统分类」徽标 + 原文提示「系统分类，用于财务报表口径」 | `AC-B3.3` / 边界 M2 的原话 |
 * | 🆕 恢复按钮 | 「**恢复**」+ 分组说明「已恢复到分类列表，历史记账不受影响」 | 用户最担心的是"恢复之后是不是要重记一遍"，不写清就会去新建同名分类（`DR-070` D1） |
 * | 层级 | L2 的「方向」不出现（跟着父级走） | `AC-B3.5`：子 `kind` 强制继承父级 |
 */

import { Fragment, useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  CATEGORY_NAME_MAX,
  CATEGORY_SPARE_ICONS,
  CategoryKind,
  type CategorySpareIcon,
  type CategoryView,
} from '@jiaying/shared';

/** 新建表单的层级选择：`null` = 建 L1；否则是某个 L1 的 id（在其下建 L2） */
type ParentChoice = string | null;

export interface CategoryManageBodyProps {
  /** 服务端下发的**全量**分类（含两个方向；本组件按 `kind` 过滤）—— **不含已移除项** */
  readonly items: readonly CategoryView[];
  /** 当前方向 —— 只影响列表过滤，切换方向不必重取（全量已在手） */
  readonly kind: CategoryKind;
  /**
   * 🆕 批 `122`：**已移除**的分类（`removed === true`，`DR-070` D1）。
   *
   * 🔴 由调用方注入（`CategoryPicker` 用 `listCategoriesWithRemoved()` 取）——
   * 本组件保持**零 IO**（否则 spec 无法静态渲染，铁律 121 的同一条理由）。
   * 缺省 `[]` = 没有已移除项 ⇒ 开关显示「含已移除（0）」且不可点。
   */
  readonly removedItems?: readonly CategoryView[];
  /**
   * 🆕 批 `122`：开关的**初始状态**（默认关）。
   *
   * 与 `defaultValue` 同性质：只在首次挂载生效，之后由内部 `useState` 接管。
   * ⚠️ 存在的理由是**可静态断言** —— 无头基座测不了点击，只能从初值验两种形态。
   */
  readonly defaultShowRemoved?: boolean;
  readonly onCreate: (input: {
    parentId: ParentChoice;
    kind?: CategoryKind;
    name: string;
    iconToken: CategorySpareIcon | null;
  }) => Promise<void>;
  readonly onUpdate: (id: string, input: { name: string }) => Promise<void>;
  readonly onRemove: (id: string) => Promise<void>;
  /** 🆕 批 `122`：恢复（`hidden = 1 → 0`）。失败时服务端会给「先恢复大类」这类可读提示 */
  readonly onRestore: (id: string) => Promise<void>;
}

/**
 * 一条分类行（L1 顶格 / L2 缩进）。
 *
 * 🔴 系统分类**不给操作按钮**（不是"点了再拦"）—— `AC-B3.3` 要求"拦截 + 提示"，
 * 但让用户先点再吃 403 是更差的体验；置灰 + 徽标已经说明了原因。
 */
function CategoryRow({
  item,
  indent,
  onRename,
  onRemove,
  onRestore,
}: {
  readonly item: CategoryView;
  readonly indent: boolean;
  readonly onRename: (item: CategoryView) => void;
  readonly onRemove: (item: CategoryView) => void;
  readonly onRestore: (item: CategoryView) => void;
}): ReactElement {
  /**
   * 🆕 批 `122`：**已移除**的行只有「恢复」一个动作（`DR-070` D1）。
   *
   * 🔴 不给它「改名 / 移除」：已移除项改名对用户没有意义（列表里看不到），
   * 而"移除一个已移除的"更是空操作 —— 两个按钮只会让人以为还有别的路。
   */
  const removed = item.removed === true;
  return (
    <ListItem
      data-testid={`manage-row-${item.id}`}
      disableGutters
      sx={{ pl: indent ? 3 : 0 }}
      secondaryAction={
        removed ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Chip
              size="small"
              variant="outlined"
              label="已移除"
              data-testid={`manage-removed-${item.id}`}
            />
            <Button size="small" data-testid={`manage-restore-${item.id}`} onClick={() => onRestore(item)}>
              恢复
            </Button>
          </Stack>
        ) : item.system ? (
          <Chip size="small" variant="outlined" label="系统分类" data-testid={`manage-sys-${item.id}`} />
        ) : (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" data-testid={`manage-rename-${item.id}`} onClick={() => onRename(item)}>
              改名
            </Button>
            <Button
              size="small"
              color="warning"
              data-testid={`manage-remove-${item.id}`}
              onClick={() => onRemove(item)}
            >
              移除
            </Button>
          </Stack>
        )
      }
    >
      <ListItemText
        primary={item.name}
        secondary={item.iconToken === null ? undefined : `图标：${item.iconToken}`}
      />
    </ListItem>
  );
}

export function CategoryManageBody({
  items,
  kind,
  removedItems = [],
  defaultShowRemoved = false,
  onCreate,
  onUpdate,
  onRemove,
  onRestore,
}: CategoryManageBodyProps): ReactElement {
  const [formOpen, setFormOpen] = useState(false);
  const [parent, setParent] = useState<ParentChoice>(null);
  const [l1Kind, setL1Kind] = useState<CategoryKind>(kind);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<CategorySpareIcon | ''>('');
  const [rename, setRename] = useState<{ id: string; name: string } | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 🆕 批 `122`：「含已移除」开关（默认关 —— 绝大多数时候没人要恢复，混进来只会让列表变脏） */
  const [showRemoved, setShowRemoved] = useState(defaultShowRemoved);

  const rows = items.filter((c) => c.kind === kind);
  const l1 = rows.filter((c) => c.level === 1);
  const childrenOf = (parentId: string): CategoryView[] => rows.filter((c) => c.parentId === parentId);
  /** 同方向下的已移除项（`N` 与列表里能看到的条数一致 —— 不报"全局已移除数"） */
  const removed = removedItems.filter((c) => c.kind === kind);

  const trimmed = name.trim();
  const nameOk = trimmed !== '' && trimmed.length <= CATEGORY_NAME_MAX;

  async function submitCreate(): Promise<void> {
    if (!nameOk) return;
    setPending(true);
    setError(null);
    try {
      await onCreate({
        parentId: parent,
        // 建 L1 才需要方向；建 L2 由服务端强制取父级（这里传了也无害）
        ...(parent === null ? { kind: l1Kind } : {}),
        name: trimmed,
        iconToken: icon === '' ? null : icon,
      });
      setName('');
      setIcon('');
      setFormOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : '新建失败');
    } finally {
      setPending(false);
    }
  }

  async function submitRename(): Promise<void> {
    if (rename === null) return;
    const next = rename.name.trim();
    if (next === '' || next.length > CATEGORY_NAME_MAX) return;
    setPending(true);
    setError(null);
    try {
      await onUpdate(rename.id, { name: next });
      setRename(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '改名失败');
    } finally {
      setPending(false);
    }
  }

  async function submitRemove(item: CategoryView): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await onRemove(item.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : '移除失败');
    } finally {
      setPending(false);
    }
  }

  /**
   * 🆕 批 `122`：恢复（`DR-070`）。
   *
   * 🔴 `409` 的两条业务分支（"大类已移除" / "额度已满"）走**同一个错误位**，
   * 直接把服务端 `message` 显示出来 —— 端侧不自编文案，否则两边措辞会分叉（铁律 24）。
   */
  async function submitRestore(item: CategoryView): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await onRestore(item.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : '恢复失败');
    } finally {
      setPending(false);
    }
  }

  return (
    <Box data-testid="category-manage-body" sx={{ width: 420 }}>
      {error !== null && (
        <Alert severity="error" sx={{ mb: 1 }} data-testid="manage-error">
          {error}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        🔴 移除只是从列表里收起：已记过的账不受影响，历史里仍显示该分类。
      </Typography>

      {/*
        🆕 批 `122`：**「含已移除（N）」开关**（`DR-070` D1）。
        🔴 默认关；`N` = 当前方向下已移除的条数。没有已移除项时置灰 ——
        点开一个"空的分组"只会让人以为分类丢了（`AC-B3.13` 的信任面）。
      */}
      <FormControlLabel
        data-testid="manage-show-removed"
        sx={{ display: 'flex', mb: 0.5 }}
        control={
          <Switch
            size="small"
            checked={showRemoved}
            disabled={removed.length === 0}
            onChange={(e) => {
              setShowRemoved(e.target.checked);
            }}
          />
        }
        label={`含已移除（${String(removed.length)}）`}
      />

      <List dense disablePadding data-testid="manage-list">
        {l1.map((p) => (
          /* 🔴 用 Fragment 而不是 `<Box component="li">` —— `ListItem` 本身渲染 `<li>`，
             外面再套一个 `<li>` 是非法嵌套（`<li>` 里只能放流内容，不能直接放另一个 `<li>` 兄弟） */
          <Fragment key={p.id}>
            <CategoryRow
              item={p}
              indent={false}
              onRename={(it) => {
                setRename({ id: it.id, name: it.name });
              }}
              onRemove={(it) => {
                void submitRemove(it);
              }}
              onRestore={(it) => {
                void submitRestore(it);
              }}
            />
            {childrenOf(p.id).map((c) => (
              <CategoryRow
                key={c.id}
                item={c}
                indent
                onRename={(it) => {
                  setRename({ id: it.id, name: it.name });
                }}
                onRemove={(it) => {
                  void submitRemove(it);
                }}
                onRestore={(it) => {
                  void submitRestore(it);
                }}
              />
            ))}
          </Fragment>
        ))}
      </List>

      {/*
        🆕 批 `122`：**已移除分组**（只在开关打开且有内容时出现）。
        🔴 已移除的 L1 与它的 L2 一起列在这里，**父子关系不动** —— 恢复被移除父级下的子类会吃 409
        并指路先恢复大类（`DR-070` D3）；把子类藏起来反而让人以为它消失了。
      */}
      {showRemoved && removed.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" data-testid="manage-removed-hint">
            已恢复到分类列表，历史记账不受影响。
          </Typography>
          <List dense disablePadding data-testid="manage-removed-list">
            {removed.map((r) => (
              <CategoryRow
                key={r.id}
                item={r}
                indent={r.level === 2}
                onRename={(it) => {
                  setRename({ id: it.id, name: it.name });
                }}
                onRemove={(it) => {
                  void submitRemove(it);
                }}
                onRestore={(it) => {
                  void submitRestore(it);
                }}
              />
            ))}
          </List>
        </>
      )}

      <Divider sx={{ my: 1 }} />

      {rename !== null && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TextField
            size="small"
            label="新名字"
            value={rename.name}
            onChange={(e) => {
              setRename({ id: rename.id, name: e.target.value });
            }}
            inputProps={{ 'data-testid': 'manage-rename-input' }}
          />
          <Button size="small" variant="contained" disabled={pending} onClick={() => void submitRename()}>
            保存
          </Button>
          <Button size="small" onClick={() => setRename(null)}>
            取消
          </Button>
        </Stack>
      )}

      {!formOpen ? (
        <Button size="small" data-testid="manage-new" onClick={() => setFormOpen(true)}>
          + 新建分类
        </Button>
      ) : (
        <Stack spacing={1} data-testid="manage-form">
          <FormControl size="small" fullWidth>
            <InputLabel id="cat-parent-label">建在哪</InputLabel>
            <Select
              labelId="cat-parent-label"
              label="建在哪"
              value={parent ?? '__root__'}
              onChange={(e) => {
                setParent(e.target.value === '__root__' ? null : e.target.value);
              }}
              inputProps={{ 'data-testid': 'manage-parent' }}
            >
              <MenuItem value="__root__">作为新的一级分类</MenuItem>
              {l1.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  在「{p.name}」下面
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {parent === null && (
            <FormControl size="small" fullWidth>
              <InputLabel id="cat-kind-label">支出还是收入</InputLabel>
              <Select
                labelId="cat-kind-label"
                label="支出还是收入"
                value={l1Kind}
                onChange={(e) => {
                  setL1Kind(e.target.value as CategoryKind);
                }}
                inputProps={{ 'data-testid': 'manage-kind' }}
              >
                <MenuItem value={CategoryKind.Expense}>支出</MenuItem>
                <MenuItem value={CategoryKind.Income}>收入</MenuItem>
              </Select>
            </FormControl>
          )}

          <TextField
            size="small"
            required
            label="名字"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            error={name !== '' && !nameOk}
            helperText={name !== '' && !nameOk ? `最多 ${String(CATEGORY_NAME_MAX)} 个字` : undefined}
            inputProps={{ 'data-testid': 'manage-name' }}
          />

          <FormControl size="small" fullWidth>
            <InputLabel id="cat-icon-label">图标（可选）</InputLabel>
            <Select
              labelId="cat-icon-label"
              label="图标（可选）"
              value={icon}
              onChange={(e) => {
                setIcon(e.target.value === '' ? '' : (e.target.value as CategorySpareIcon));
              }}
              inputProps={{ 'data-testid': 'manage-icon' }}
            >
              <MenuItem value="">不用图标</MenuItem>
              {CATEGORY_SPARE_ICONS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={pending || !nameOk}
              data-testid="manage-submit"
              onClick={() => void submitCreate()}
            >
              建好了
            </Button>
            <Button
              size="small"
              onClick={() => {
                setFormOpen(false);
                setName('');
              }}
            >
              取消
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

export interface CategoryManageDialogProps extends Omit<CategoryManageBodyProps, 'onClose'> {
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * 外壳：只管 `Dialog` 与关闭，内容全在 `CategoryManageBody`（见文件头铁律 121）。
 */
export function CategoryManageDialog({
  open,
  onClose,
  ...body
}: CategoryManageDialogProps): ReactElement {
  return (
    <Dialog open={open} onClose={onClose} data-testid="category-manage-dialog">
      <DialogTitle>管理分类</DialogTitle>
      <DialogContent>
        <CategoryManageBody {...body} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="manage-close">
          关掉
        </Button>
      </DialogActions>
    </Dialog>
  );
}
